import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import Toolbar from './Toolbar';
import ErrorBoundary from '~components/common/ErrorBoundary';
import { usePushChannel } from '~hooks/usePushChannel';

export default function PageLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const noPadding = location.pathname === '/dashboard';

  // Subscribe to the OneService server push stream for this session.
  usePushChannel();

  return (
    <Layout>
      <Toolbar />

      <Main>
        <div id="sidebar-slot">
          {/* Sidebar is rendered in here via React portal */}
        </div>

        <Scroller>
          <div id="breadcrumbs-slot">
            {/* Breadcrumbs are rendered in here via React portal */}
          </div>

          <Content noPadding={noPadding}>
            {/* Contain per-page render crashes so the shell stays usable;
                key by route so navigating away clears the error state. */}
            <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
          </Content>
        </Scroller>
      </Main>
    </Layout>
  );
}

const Layout = styled.div`
  position: relative;
  overflow: hidden;
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  background-color: ${p => p.theme.colors.background};

  @supports (-webkit-touch-callout: none) {
    height: -webkit-fill-available;
  }
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  overflow: auto;
  min-height: 0px;
`;

const Scroller = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Content = styled.main<{ noPadding?: boolean }>`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${p => p.theme.spacing.medium}px ${p => p.theme.spacing.medium}px;

  ${p => p.theme.media.tablet} {
    padding: ${p => (p.noPadding ? 0 : p.theme.spacing.medium)}px;
  }

  ${p => p.theme.media.phone} {
    padding: ${p => (p.noPadding ? 0 : p.theme.spacing.medium)}px;
  }

  ${p => p.noPadding && 'padding: 0;'}
`;
